/**
 * Server-side rate limiting via Supabase RPC.
 *
 * Uses a `rate_limits` table and `check_rate_limit` RPC function.
 * This module provides the client-side caller and the SQL migration.
 */

// ─── Minimal Supabase interface ─────────────────────────────────────────────
interface MinimalSupabase {
  rpc: (
    fn: string,
    params: Record<string, unknown>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => PromiseLike<{ data: any; error: any }>;
}

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  retryAfterSeconds: number | null;
}

/**
 * Check a server-side rate limit.
 *
 * @param supabase   - Supabase client
 * @param key        - Rate limit key (e.g. "login:user@example.com", "sms:+6591234567")
 * @param limitType  - Predefined limit type: "login" | "sms_venue" | "otp_consumer"
 * @returns Whether the action is allowed
 */
export async function checkRateLimit(
  supabase: MinimalSupabase,
  key: string,
  limitType:
    | "login"
    | "sms_venue"
    | "otp_consumer"
    | "qr_generate"
    | "qr_generate_cooldown",
): Promise<RateLimitResult> {
  try {
    const rpcPromise = supabase.rpc("check_rate_limit", {
      p_key: key,
      p_limit_type: limitType,
    });
    const timeoutPromise = new Promise<{ data: null; error: Error }>((resolve) =>
      setTimeout(() => resolve({ data: null, error: new Error("rate-limit timeout") }), 5000),
    );
    const { data, error } = await Promise.race([rpcPromise, timeoutPromise]);

    if (error) {
      console.warn("[rate-limit] RPC error:", error);
      // Fail open — don't block users if rate limiting infra is down
      return { allowed: true, remaining: 0, retryAfterSeconds: null };
    }

    const result = data as {
      allowed: boolean;
      remaining: number;
      retry_after_seconds: number | null;
    };

    return {
      allowed: result.allowed,
      remaining: result.remaining,
      retryAfterSeconds: result.retry_after_seconds,
    };
  } catch (err) {
    console.warn("[rate-limit] Unexpected error:", err);
    return { allowed: true, remaining: 0, retryAfterSeconds: null };
  }
}

/**
 * Reset a server-side rate limit after successful action (e.g. login).
 *
 * @param supabase - Supabase client
 * @param key      - Rate limit key to reset (e.g. "otp:+6591234567")
 */
export async function resetRateLimit(
  supabase: MinimalSupabase,
  key: string,
): Promise<void> {
  try {
    const rpcPromise = supabase.rpc("reset_rate_limit", { p_key: key });
    const timeoutPromise = new Promise<{ error: Error }>((resolve) =>
      setTimeout(
        () => resolve({ error: new Error("reset-rate-limit timeout") }),
        5000,
      ),
    );
    await Promise.race([rpcPromise, timeoutPromise]);
  } catch (err) {
    console.warn("[rate-limit] Failed to reset rate limit:", err);
  }
}

/**
 * SQL migration for rate limiting.
 *
 * Run this against your Supabase database to create the
 * `rate_limits` table and `check_rate_limit` function.
 */
export const RATE_LIMIT_MIGRATION = `
-- Rate limits table
CREATE TABLE IF NOT EXISTS public.rate_limits (
  id          uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  key         text NOT NULL,
  attempts    integer NOT NULL DEFAULT 0,
  window_start timestamptz NOT NULL DEFAULT now(),
  created_at  timestamptz NOT NULL DEFAULT now()
);

CREATE UNIQUE INDEX IF NOT EXISTS idx_rate_limits_key ON public.rate_limits (key);

-- RPC function: check_rate_limit
-- Returns: { allowed: boolean, remaining: integer, retry_after_seconds: integer }
CREATE OR REPLACE FUNCTION public.check_rate_limit(
  p_key text,
  p_limit_type text
) RETURNS jsonb
LANGUAGE plpgsql
SECURITY DEFINER
AS $$
DECLARE
  v_max_attempts integer;
  v_window_seconds integer;
  v_record public.rate_limits%ROWTYPE;
  v_now timestamptz := now();
  v_window_start timestamptz;
  v_remaining integer;
  v_retry_after integer;
BEGIN
  -- Define limits by type
  CASE p_limit_type
    WHEN 'login' THEN
      v_max_attempts := 5;
      v_window_seconds := 900; -- 15 minutes
    WHEN 'sms_venue' THEN
      v_max_attempts := 3;
      v_window_seconds := 900; -- 15 minutes
    WHEN 'otp_consumer' THEN
      v_max_attempts := 5;
      v_window_seconds := 3600; -- 1 hour
    ELSE
      v_max_attempts := 10;
      v_window_seconds := 900;
  END CASE;

  v_window_start := v_now - (v_window_seconds || ' seconds')::interval;

  -- Upsert the rate limit record
  INSERT INTO public.rate_limits (key, attempts, window_start)
  VALUES (p_key, 1, v_now)
  ON CONFLICT (key) DO UPDATE
  SET
    attempts = CASE
      WHEN public.rate_limits.window_start < v_window_start THEN 1
      ELSE public.rate_limits.attempts + 1
    END,
    window_start = CASE
      WHEN public.rate_limits.window_start < v_window_start THEN v_now
      ELSE public.rate_limits.window_start
    END
  RETURNING * INTO v_record;

  v_remaining := GREATEST(0, v_max_attempts - v_record.attempts);

  IF v_record.attempts > v_max_attempts THEN
    v_retry_after := v_window_seconds - EXTRACT(EPOCH FROM (v_now - v_record.window_start))::integer;
    RETURN jsonb_build_object(
      'allowed', false,
      'remaining', 0,
      'retry_after_seconds', GREATEST(0, v_retry_after)
    );
  END IF;

  RETURN jsonb_build_object(
    'allowed', true,
    'remaining', v_remaining,
    'retry_after_seconds', null
  );
END;
$$;

-- Allow anon + authenticated to call the RPC
GRANT EXECUTE ON FUNCTION public.check_rate_limit(text, text) TO anon, authenticated;

-- RLS on rate_limits (only service role can read directly)
ALTER TABLE public.rate_limits ENABLE ROW LEVEL SECURITY;
`;
